/**
 * Aula invertida (M5) — tipos + selectores PUROS del plan de clase.
 *
 * Un `session_module` enlaza una `class_session` con un `course_module` bajo una
 * FASE (antes/durante/después). Este archivo concentra la lógica pura y testeable
 * (agrupar por fase, ordenar, componer los ticks de completitud reutilizando el
 * progreso que ya existe: lesson_progress, assignment_submission,
 * video_watch_progress). La UI (Solid) y el write-path (Rust) la consumen.
 */

/** Fase del aula invertida (== `session_module.phase`). */
export type PlanPhase = "pre" | "live" | "post";

/** Las tres fases en orden pedagógico (pre → live → post). */
export const PLAN_PHASES: readonly PlanPhase[] = ["pre", "live", "post"] as const;

/** Rango canónico de una fase (pre=0, live=1, post=2). */
export function phaseRank(phase: PlanPhase): number {
  return PLAN_PHASES.indexOf(phase);
}

/** Type guard: ¿es una fase válida? (el write-path rechaza las demás). */
export function isPlanPhase(value: unknown): value is PlanPhase {
  return value === "pre" || value === "live" || value === "post";
}

/**
 * Ítem del plan de clase = fila `session_module` compuesta con el `course_module`
 * que referencia (kind/ref_id/title) para poder renderizarlo y resolver su tick.
 */
export interface PlanModule {
  /** id de la fila session_module. */
  id: string;
  /** id del course_module referenciado. */
  moduleId: string;
  phase: PlanPhase;
  position: number;
  /** kind del course_module (lesson|resource|video|assignment|forum|live_session…). */
  kind: string | null;
  /** ref_id del course_module (lesson.id | assignment.id | video_asset.id | …). */
  refId: string | null;
  /** Título legible del módulo (para la lista). */
  title?: string | null;
  /** Nota de encuadre por ítem (session_module.config.note). */
  note?: string | null;
}

/**
 * Fuentes de progreso ya existentes en el cliente (local-first), indexadas por la
 * clave natural de cada kind. Todas opcionales: un kind sin fuente ⇒ sin tick.
 */
export interface PlanCompletionCtx {
  /** lesson_progress.status por lesson id (ref_id de un módulo `lesson`). */
  lessonStatusByRef?: ReadonlyMap<string, string | null>;
  /** ¿entregada? por assignment id (ref_id de un módulo `assignment`). */
  submittedByAssignment?: ReadonlyMap<string, boolean>;
  /** ¿vídeo completado? por video_asset id (ref_id de un módulo `video`). */
  videoDoneByRef?: ReadonlyMap<string, boolean>;
}

/** Estado de completitud de un ítem: hecho | pendiente | null (no puntúa/informativo). */
export type ItemCompletion = "done" | "pending" | null;

/**
 * Resuelve el tick de un ítem reutilizando el progreso existente. `null` cuando
 * el kind no tiene concepto de completitud (recurso, foro, clase en vivo): esos
 * ítems son informativos y no cuentan para el progreso del plan.
 */
export function itemCompletion(mod: PlanModule, ctx: PlanCompletionCtx): ItemCompletion {
  if (!mod.refId) {
    return null;
  }
  switch (mod.kind) {
    case "lesson": {
      return ctx.lessonStatusByRef?.get(mod.refId) === "completed" ? "done" : "pending";
    }
    case "assignment": {
      return ctx.submittedByAssignment?.get(mod.refId) ? "done" : "pending";
    }
    case "video": {
      return ctx.videoDoneByRef?.get(mod.refId) ? "done" : "pending";
    }
    default: {
      // resource | forum | live_session | kinds futuros → sin tick.
      return null;
    }
  }
}

/** Comparador estable: fase (pre→live→post) → posición → id. */
export function comparePlanModules(a: PlanModule, b: PlanModule): number {
  const byPhase = phaseRank(a.phase) - phaseRank(b.phase);
  if (byPhase !== 0) {
    return byPhase;
  }
  const byPos = a.position - b.position;
  if (byPos !== 0) {
    return byPos;
  }
  if (a.id < b.id) {
    return -1;
  }
  if (a.id > b.id) {
    return 1;
  }
  return 0;
}

/** Agrupa el plan por fase (cada grupo ordenado por posición). No muta la entrada. */
export function groupPlanByPhase(mods: readonly PlanModule[]): Record<PlanPhase, PlanModule[]> {
  const out: Record<PlanPhase, PlanModule[]> = { pre: [], live: [], post: [] };
  for (const m of mods.toSorted(comparePlanModules)) {
    out[m.phase].push(m);
  }
  return out;
}

/**
 * Progreso del plan (o de una fase): `done` = ítems con tick "done"; `total` =
 * ítems que puntúan (completion no-null). Los informativos no cuentan.
 */
export function planProgress(
  mods: readonly PlanModule[],
  ctx: PlanCompletionCtx,
): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const m of mods) {
    const c = itemCompletion(m, ctx);
    if (c === null) {
      continue;
    }
    total += 1;
    if (c === "done") {
      done += 1;
    }
  }
  return { done, total };
}
